import DateTimeUtil from "utils/DateTimeUtil.js";
import {getDownloadURLFromStorage} from "./FirebaseDbService.js";

export interface PrivacyOptions {
  challenges: boolean;
  explore: boolean;
  publicProfile: boolean;
  exploreSuper: boolean;
  feedMap: boolean;
}

export class AppUser {
  uid: string;
  img: any;
  userInDb: any;
  email: string;
  name: string;
  displayName: string;
  dob: any;
  subscriptionExpiryDateInUnixTimestamp: number;
  subscription: any;
  latestFeaturesVersion: string;
  isUserSeenNewFeatures: boolean;
  gpsSpeedsurfing: any;
  garminConnect: any;
  coros: any;
  suunto: any;
  strava: any;
  privacyOptions: PrivacyOptions;
  following: any;
  followers: any;

  constructor(user: any, userInDb: any) {
    if (userInDb) {
      this.uid = userInDb.ID || user.uid;
      this.img = user.img;
      this.userInDb = userInDb;
      this.email = userInDb.email;
      const resolvedName = userInDb.displayName || userInDb.name || "";
      this.name = resolvedName;
      this.displayName = resolvedName;
      this.dob = userInDb.bod;

      // Handle new subscription structure (timestamps in seconds)
      if (userInDb.subscription?.premium?.expiration || userInDb.subscription?.ultra?.expiration) {
        const premiumExpiration = userInDb.subscription.premium?.expiration; // seconds
        const ultraExpiration = userInDb.subscription.ultra?.expiration; // seconds

        // Use the latest valid (non-expired) expiration date
        const now = new Date().getTime() / 1000;
        const premiumValid = premiumExpiration && premiumExpiration >= now;
        const ultraValid = ultraExpiration && ultraExpiration >= now;

        if (ultraValid && premiumValid) {
          // Both valid, use the later one
          this.subscriptionExpiryDateInUnixTimestamp = Math.max(ultraExpiration, premiumExpiration);
        } else if (ultraValid) {
          this.subscriptionExpiryDateInUnixTimestamp = ultraExpiration;
        } else if (premiumValid) {
          this.subscriptionExpiryDateInUnixTimestamp = premiumExpiration;
        } else {
          // Neither valid, fallback to legacy or 0
          this.subscriptionExpiryDateInUnixTimestamp = userInDb.subscriptionExpDate || 0;
        }

        // Store subscription tier info
        this.subscription = userInDb.subscription;
      } else {
        // Fallback to legacy field
        this.subscriptionExpiryDateInUnixTimestamp = userInDb.subscriptionExpDate;
      }

      this.latestFeaturesVersion = "v2";
      this.isUserSeenNewFeatures = this.isSeenNewFeatures(userInDb);
      this.gpsSpeedsurfing = userInDb.gpsSpeedsurfing;
      this.garminConnect = userInDb.garminConnect;
      this.coros = userInDb.coros;
      this.suunto = userInDb.suunto;
      this.strava = userInDb.strava;
      this.privacyOptions = userInDb.privacyOptions || {
        challenges: false,
        explore: false,
        publicProfile: false,
        exploreSuper: false,
        feedMap: false
      };
      this.following = userInDb.following;
      this.followers = userInDb.followers;
    } else {
      return null;
    }
  }

  get isUserPro(): boolean {
    return isProUser(this.userInDb);
  }

  async getImage(): Promise<string | null> {
    try {
      const imageUrl = await getDownloadURLFromStorage(`userPictures/img${this.uid}.jpg`);
      return imageUrl || null;
    } catch (e) {
      console.error("Error getting user image:", e);
      return null;
    }
  }

  isSeenNewFeatures(userInDb: any): boolean {
    return true;
  }

  seenNewFeatures(): void {
    this.isUserSeenNewFeatures = true;
  }
}

export const isProUser = (user: any, whenInSeconds: number | undefined = undefined): boolean => {
  const twentyFourHourInSeconds = 86400;
  const date = whenInSeconds ? new Date(whenInSeconds * 1000) : new Date();

  // Check new subscription structure first (timestamps in seconds)
  if (user?.subscription?.premium?.expiration || user?.subscription?.ultra?.expiration) {
    const premiumExpiration = user.subscription.premium?.expiration; // seconds
    const ultraExpiration = user.subscription.ultra?.expiration; // seconds
    const proExpiration = user.subscription.pro?.expiration; // seconds

    // Check which subscription is actually active (not expired)
    const premiumActive = premiumExpiration && new Date(premiumExpiration * 1000).getTime() >= date.getTime();
    const ultraActive = ultraExpiration && new Date(ultraExpiration * 1000).getTime() >= date.getTime();
    const proActive = proExpiration && new Date(proExpiration * 1000).getTime() >= date.getTime();

    if (premiumActive || ultraActive || proActive) {
      return true;
    }

    // Falls through to the legacy field: a tier node can be present but lapsed, and
    // must not override a `subscriptionExpDate` that is still valid.
  }

  // Fallback to legacy subscriptionExpDate field
  const subscriptionExpDate = user?.subscriptionExpDate || 0;

  // If subscriptionExpDate is 0 or null, user is not pro
  if (!subscriptionExpDate) {
    return false;
  }

  const userSubscriptionExpiryDate = DateTimeUtil.getDateByTimeStamp(subscriptionExpDate + twentyFourHourInSeconds);
  const isPro = userSubscriptionExpiryDate.getTime() >= date.getTime();
  return isPro;
};
